(() => {
  'use strict';

  const GLOBAL_FLAG = '__entrevoltasSamePrintAutoLoaded';
  const HOST_SELECTOR = '[data-ev-same-print-auto]';
  const PRODUCT_LIMIT = 10;

  if (window[GLOBAL_FLAG]) return;
  window[GLOBAL_FLAG] = true;

  const normalize = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR');

  const parseTitle = (title) => {
    const value = String(title || '').trim();
    const match = value.match(/\s+(?:—|–|-)\s+/);

    if (!match || match.index == null) {
      return { model: '', stamp: '' };
    }

    const model = value.slice(0, match.index).trim();
    const stamp = value.slice(match.index + match[0].length).trim();

    return { model, stamp };
  };

  const typePriority = (type) => {
    const order = [
      'camiseta unissex',
      'camiseta oversized',
      'camiseta algodao peruano',
      'regata unissex',
      'cropped',
      'moletom cropped',
      'sueter moletom',
      'hoodie moletom',
      'camiseta infantil',
      'body infantil',
    ];

    const index = order.indexOf(normalize(type));
    return index === -1 ? 100 : index;
  };

  const canonicalProductUrl = (rawUrl) => {
    try {
      const url = new URL(rawUrl, window.location.origin);
      return url.pathname;
    } catch {
      return String(rawUrl || '').split('?')[0];
    }
  };

  const formatMoney = (value) => {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return '';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const createPill = (product, stamp) => {
    const parsed = parseTitle(product.title);
    const model = String(product.type || parsed.model || 'Outra peça').trim();
    const anchor = document.createElement('a');

    anchor.className = 'ev-same-print__pill';
    anchor.setAttribute('role', 'listitem');
    anchor.href = canonicalProductUrl(product.url);
    anchor.setAttribute('aria-label', `${model} — mesma estampa ${stamp}`);
    anchor.textContent = model;

    if (product.available === false) {
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = ' — indisponível no momento';
      anchor.appendChild(hidden);
    }

    return anchor;
  };

  const modalMarkup = ({ product, stamp, showImages }) => {
    const parsed = parseTitle(product.title);
    const model = String(product.type || parsed.model || 'Outra peça').trim();
    const imageUrl =
      product.featured_image?.url ||
      product.image ||
      '';
    const imageAlt = product.featured_image?.alt || product.title || model;
    const price = formatMoney(product.price_min || product.price);
    const priceMax = Number.parseFloat(product.price_max);
    const priceMin = Number.parseFloat(product.price_min || product.price);
    const varies =
      Number.isFinite(priceMax) &&
      Number.isFinite(priceMin) &&
      Math.abs(priceMax - priceMin) > 0.001;

    return `
      <a
        class="ev-same-print-modal__row"
        role="listitem"
        href="${canonicalProductUrl(product.url)}"
        aria-label="${model.replaceAll('"', '&quot;')} — mesma estampa ${stamp.replaceAll('"', '&quot;')}"
      >
        ${showImages && imageUrl ? `
          <span class="ev-same-print-modal__thumb">
            <img src="${imageUrl}" loading="lazy" alt="${String(imageAlt).replaceAll('"', '&quot;')}">
          </span>
        ` : ''}
        <span class="ev-same-print-modal__body">
          <strong>${model}</strong>
          ${price ? `<span class="ev-same-print-modal__price">${varies ? 'A partir de ' : ''}${price}</span>` : ''}
          ${product.available === false ? '<span class="ev-same-print-modal__availability">Indisponível no momento</span>' : ''}
        </span>
      </a>
    `;
  };

  const removeDynamicModal = (host) => {
    host.querySelectorAll('.ev-same-print-modal[data-ev-auto-modal]').forEach((modal) => modal.remove());
  };

  const render = (host, products) => {
    const stamp = String(host.dataset.evStamp || '').trim();
    const heading = String(host.dataset.evHeading || 'Esta estampa em outras peças').trim();
    const showImages = host.dataset.evShowImages !== 'false';
    const direct = host.querySelector('.ev-same-print__direct');

    if (!direct || !stamp || !products.length) return false;

    direct.replaceChildren();
    removeDynamicModal(host);

    products.slice(0, 2).forEach((product) => {
      direct.appendChild(createPill(product, stamp));
    });

    if (products.length > 2) {
      const extraCount = products.length - 2;
      const currentId = String(host.dataset.evCurrentProductId || 'product');
      const modalId = `EVSamePrintBrowser-${currentId}`;
      const opener = document.createElement('span');
      opener.className = 'ev-same-print__opener no-js-hidden';
      opener.innerHTML = `
        <button
          type="button"
          class="ev-same-print__more link"
          aria-haspopup="dialog"
          aria-controls="${modalId}"
          aria-expanded="false"
        >+${extraCount} outras peças</button>
      `;
      direct.appendChild(opener);
    }
    const headingNode = host.querySelector('.ev-same-print__heading');
    if (headingNode) headingNode.textContent = heading;

    host.hidden = false;
    host.dataset.evAutoReady = 'true';
    host.dataset.evAutoCount = String(products.length);

    return true;
  };

  const fetchRelated = async (host) => {
    if (host.dataset.evRegistry === 'true') {
      host.dataset.evAutoReady = 'registry';
      return;
    }
    if (host.dataset.evAutoLoading === 'true' || host.dataset.evAutoReady === 'true') return;

    const stamp = String(host.dataset.evStamp || '').trim();
    const currentId = String(host.dataset.evCurrentProductId || '').trim();
    const searchUrl = String(host.dataset.evSearchUrl || '/search/suggest.json').trim();

    if (!stamp || !currentId) return;

    host.dataset.evAutoLoading = 'true';

    try {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', stamp);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(PRODUCT_LIMIT));
      url.searchParams.set('resources[options][unavailable_products]', 'show');

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Predictive search returned ${response.status}`);
      }

      const payload = await response.json();
      const source = payload?.resources?.results?.products || [];
      const stampKey = normalize(stamp);
      const seen = new Set();

      const related = source
        .filter((product) => {
          const id = String(product.id || '');
          const parsed = parseTitle(product.title);

          if (!id || id === currentId || normalize(parsed.stamp) !== stampKey) return false;
          if (seen.has(id)) return false;

          seen.add(id);
          return true;
        })
        .sort((a, b) => {
          const rank = typePriority(a.type) - typePriority(b.type);
          if (rank !== 0) return rank;
          return String(a.title || '').localeCompare(String(b.title || ''), 'pt-BR');
        });

      if (!related.length) {
        const staticLinks = host.querySelectorAll('.ev-same-print__pill').length;
        if (!staticLinks) host.hidden = true;
        return;
      }

      render(host, related);
    } catch (error) {
      console.warn('[Entrevoltas] Relações automáticas da estampa indisponíveis; mantendo fallback local.', error);
    } finally {
      delete host.dataset.evAutoLoading;
    }
  };

  const init = (root = document) => {
    const hosts = [];

    if (root.matches?.(HOST_SELECTOR)) hosts.push(root);
    root.querySelectorAll?.(HOST_SELECTOR).forEach((host) => hosts.push(host));

    hosts.forEach((host) => fetchRelated(host));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  // ENTREVOLTAS SAME PRINT CLICK FIX V4
  // The theme ModalDialog constructor assumes a pre-existing ModalClose-* child.
  // Same-print modals are generated dynamically, so we use a local, resilient controller.
  const evBrowserModalState = new WeakMap();

  const closeBrowserModal = (modal) => {
    if (!modal) return;
    modal.removeAttribute('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    const opener = evBrowserModalState.get(modal);
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus?.();
      evBrowserModalState.delete(modal);
    }

  };

  const showBrowserModal = (modal, opener) => {
    evBrowserModalState.set(modal, opener);
    modal.setAttribute('open', '');
    modal.setAttribute('aria-hidden', 'false');
    opener.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    window.requestAnimationFrame(() => modal.querySelector('[data-ev-browser-modal-close]')?.focus());
  };

  const buildBrowserModal = (host, products) => {
    const stamp = String(host.dataset.evStamp || '').trim();
    const currentId = String(host.dataset.evCurrentProductId || 'product');
    const modalId = 'EVSamePrintBrowser-' + currentId;
    const modalProducts = products.slice(2);
    const extraCount = modalProducts.length;
    document.getElementById(modalId)?.remove();

    const modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'ev-same-print-browser-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = [
      '<div role="dialog" aria-modal="true" aria-labelledby="' + modalId + '-title" class="ev-same-print-browser-modal__panel" tabindex="-1">',
      '<button type="button" class="ev-same-print-browser-modal__close" data-ev-browser-modal-close aria-label="Fechar"><span aria-hidden="true">×</span></button>',
      '<div class="ev-same-print-browser-modal__header">',
      '<h2 id="' + modalId + '-title" class="ev-same-print-browser-modal__title">Mais ' + extraCount + ' peças com ' + stamp + '</h2>',
      '<p class="ev-same-print-modal__intro">Escolha a modelagem e veja os detalhes na página da peça.</p>',
      '</div>',
      '<div class="ev-same-print-modal__list" role="list">',
      modalProducts.map((product) => modalMarkup({ product, stamp, showImages: host.dataset.evShowImages !== 'false' })).join(''),
      '</div></div>'
    ].join('');

    modal.querySelectorAll('.ev-same-print-modal__body').forEach((body) => {
      const cta = document.createElement('span');
      cta.className = 'ev-same-print-modal__cta';
      cta.textContent = 'Ver produto';
      body.appendChild(cta);
    });

    document.body.appendChild(modal);
    return modal;
  };

  const fetchAllSamePrintProducts = async (host) => {
    const stamp = String(host.dataset.evStamp || '').trim();
    const currentId = String(host.dataset.evCurrentProductId || '').trim();
    const searchUrl = String(host.dataset.evSearchUrl || '/search/suggest.json').trim();
    if (!stamp || !currentId) return [];

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', stamp);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(PRODUCT_LIMIT));
    url.searchParams.set('resources[options][unavailable_products]', 'show');

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Predictive search returned ' + response.status);

    const payload = await response.json();
    const source = payload?.resources?.results?.products || [];
    const stampKey = normalize(stamp);
    const seen = new Set();

    return source.filter((product) => {
      const id = String(product.id || '');
      const parsed = parseTitle(product.title);
      if (!id || id === currentId || normalize(parsed.stamp) !== stampKey || seen.has(id)) return false;
      seen.add(id);
      return true;
    }).sort((a, b) => {
      const rank = typePriority(a.type) - typePriority(b.type);
      if (rank !== 0) return rank;
      return String(a.title || '').localeCompare(String(b.title || ''), 'pt-BR');
    });
  };

  document.addEventListener('click', async (event) => {
    const more = event.target.closest?.('.ev-same-print__more');
    if (more) {
      const host = more.closest('[data-ev-same-print-auto]');
      if (!host) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const controlledId = more.getAttribute('aria-controls');
      const existingModal = controlledId ? document.getElementById(controlledId) : null;
      if (existingModal?.classList.contains('ev-same-print-browser-modal')) {
        if (existingModal.parentElement !== document.body) document.body.appendChild(existingModal);
        showBrowserModal(existingModal, more);
        return;
      }

      try {
        more.disabled = true;
        more.setAttribute('aria-busy', 'true');
        const products = await fetchAllSamePrintProducts(host);
        if (!products.length) return;
        const modal = buildBrowserModal(host, products);
        more.setAttribute('aria-controls', modal.id);
        showBrowserModal(modal, more);
      } catch (error) {
        console.warn('[Entrevoltas] Não foi possível abrir outras peças da estampa.', error);
      } finally {
        more.disabled = false;
        more.removeAttribute('aria-busy');
      }
      return;
    }

    const close = event.target.closest?.('[data-ev-browser-modal-close]');
    if (close) {
      event.preventDefault();
      closeBrowserModal(close.closest('.ev-same-print-browser-modal'));
      return;
    }

    const modal = event.target.closest?.('.ev-same-print-browser-modal');
    if (modal && event.target === modal) closeBrowserModal(modal);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('.ev-same-print-browser-modal[open]');
    if (modal) closeBrowserModal(modal);
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();